import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Eye, AlertTriangle, Clock, CheckCircle2, FileClock } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatCard } from "@/components/ui/StatCard";
import { EmptyState } from "@/components/ui/EmptyState";
import { apiGet } from "@/lib/api";
import { ORDER_STATUS_VARIANT, PRIORITY_VARIANT } from "@/lib/rbac";
import { slaInfo, formatAOAFull, formatRel } from "@/lib/sla";
import pt from "@/lib/i18n";

const STATUS_LABEL = (s) => pt.services[`status_${s}`] || s;
const PRIORITY_LABEL = (p) => pt.services[`priority_${p}`] || p;

const ServicesPage = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [stats, setStats] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");

    const load = async () => {
        setRows(null);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (priorityFilter) params.set("priority", priorityFilter);
            if (search) params.set("search", search);
            params.set("page_size", "100");
            const [data, st] = await Promise.all([
                apiGet(`/service-orders?${params}`),
                apiGet("/service-orders/stats"),
            ]);
            setRows(data || []);
            setStats(st);
        } catch (e) {
            setRows([]);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, priorityFilter]);

    useEffect(() => {
        const id = setTimeout(load, 300);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <div data-testid="services-root">
            <PageHeader
                title={pt.services.title}
                subtitle={pt.services.subtitle}
                right={
                    <NxButton
                        variant="copper"
                        onClick={() => navigate("/servicos/nova")}
                        testId="new-order-button"
                        icon={<Plus size={16} />}
                    >
                        {pt.services.new}
                    </NxButton>
                }
            />

            {/* KPIs */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-7" data-testid="services-kpis">
                {stats === null ? (
                    [0, 1, 2, 3].map((i) => (
                        <GlassCard key={i} padding="p-6">
                            <Skeleton height="14px" width="50%" />
                            <div className="mt-4">
                                <Skeleton height="32px" width="60%" />
                            </div>
                        </GlassCard>
                    ))
                ) : (
                    <>
                        <StatCard
                            label={pt.services.kpiActivated}
                            value={stats.activated_today}
                            trend={{ direction: "up", value: 8.5 }}
                            index={0}
                            testId="kpi-activated"
                        />
                        <StatCard
                            label={pt.services.kpiPendingApproval}
                            value={stats.by_status?.pending_approval || 0}
                            trend={{ direction: "down", value: 3.1 }}
                            index={1}
                            testId="kpi-pending"
                        />
                        <StatCard
                            label={pt.services.kpiSlaAtRisk}
                            value={stats.sla_at_risk}
                            trend={{ direction: "down", value: 4.8 }}
                            index={2}
                            testId="kpi-sla-risk"
                        />
                        <StatCard
                            label={pt.services.kpiResolvedMonth}
                            value={stats.by_status?.active || 0}
                            trend={{ direction: "up", value: 14.2 }}
                            index={3}
                            testId="kpi-active-total"
                        />
                    </>
                )}
            </section>

            {/* Filters */}
            <GlassCard padding="p-4" className="mb-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <GlassInput
                        prefix={<Search size={16} />}
                        placeholder={pt.services.searchOrderPlaceholder || "Pesquisar por número de ordem…"}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        testId="services-search"
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="glass-input"
                        data-testid="services-status-filter"
                    >
                        <option value="">Todos os estados</option>
                        {Object.keys(ORDER_STATUS_VARIANT).map((s) => (
                            <option key={s} value={s}>
                                {STATUS_LABEL(s)}
                            </option>
                        ))}
                    </select>
                    <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="glass-input"
                        data-testid="services-priority-filter"
                    >
                        <option value="">Todas as prioridades</option>
                        {Object.keys(PRIORITY_VARIANT).map((p) => (
                            <option key={p} value={p}>
                                {PRIORITY_LABEL(p)}
                            </option>
                        ))}
                    </select>
                </div>
            </GlassCard>

            {/* Table */}
            {rows === null ? (
                <GlassCard padding="p-6">
                    <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} height="48px" />
                        ))}
                    </div>
                </GlassCard>
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={<FileClock size={24} />}
                    title={pt.services.empty}
                    body={pt.services.emptyHint || "Crie uma nova ordem de serviço para começar."}
                />
            ) : (
                <GlassCard padding="p-0" testId="orders-table">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[color:var(--glass-border)]">
                                    {[
                                        pt.services.col_order,
                                        pt.services.col_customer,
                                        pt.services.col_service,
                                        pt.services.col_status,
                                        pt.services.col_priority,
                                        pt.services.col_sla,
                                        pt.services.col_created,
                                        "",
                                    ].map((h, i) => (
                                        <th
                                            key={i}
                                            className="text-left px-5 py-3 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((o) => {
                                    const sla = slaInfo(o.sla_deadline);
                                    return (
                                        <tr
                                            key={o.id}
                                            className="border-b border-[color:var(--glass-border-subtle)] hover:bg-[color:var(--copper-subtle)] cursor-pointer transition-colors"
                                            onClick={() => navigate(`/servicos/${o.id}`)}
                                            data-testid={`order-row-${o.id}`}
                                        >
                                            <td className="px-5 py-3 font-mono text-xs text-[color:var(--copper-dark)]">
                                                {o.order_number}
                                            </td>
                                            <td className="px-5 py-3 font-body text-sm text-[color:var(--slate-deep)]">
                                                {o.customer?.full_name || "—"}
                                            </td>
                                            <td className="px-5 py-3 font-body text-sm text-[color:var(--slate-mid)]">
                                                {o.service?.name || "—"}
                                            </td>
                                            <td className="px-5 py-3">
                                                <NxBadge variant={ORDER_STATUS_VARIANT[o.status] || "neutral"}>
                                                    {STATUS_LABEL(o.status)}
                                                </NxBadge>
                                            </td>
                                            <td className="px-5 py-3">
                                                <NxBadge variant={PRIORITY_VARIANT[o.priority] || "neutral"}>
                                                    {PRIORITY_LABEL(o.priority)}
                                                </NxBadge>
                                            </td>
                                            <td className="px-5 py-3">
                                                <NxBadge variant={sla.variant}>
                                                    <Clock size={12} /> {sla.label}
                                                </NxBadge>
                                            </td>
                                            <td className="px-5 py-3 font-mono text-[11px] text-[color:var(--slate-light)]">
                                                {formatRel(o.created_at)}
                                            </td>
                                            <td className="px-5 py-3">
                                                <Eye size={15} className="text-[color:var(--slate-light)]" />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </GlassCard>
            )}
        </div>
    );
};

export default ServicesPage;
